import { DataTable } from "@okeys/shared-react-components"
import { ColumnDef } from "@tanstack/react-table"
import { Clock, Euro, Mail, MoreHorizontal, Phone } from "lucide-react"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu"
import { FidelityPointsCell } from "./FidelityPointsCell"
import { Client, getTypeColor, getTypeLabel } from "./utils/clientsUtils"

type ClientsTableProps = {
  clients: Client[]
}

export const ClientsTable = ({ clients }: ClientsTableProps) => {
  // Définition des colonnes pour DataTable
  const columns: ColumnDef<Client>[] = [
    {
      id: "fullName",
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      header: "Client",
      cell: ({ row }) => {
        const client = row.original
        return (
          <div className="flex flex-col">
            <div className="font-medium">
              {client.firstName} {client.lastName}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge
                variant="outline"
                className={`text-xs ${getTypeColor(client.type)}`}
              >
                {getTypeLabel(client.type)}
              </Badge>
            </div>
          </div>
        )
      },
      enableSorting: true,
    },
    {
      accessorKey: "email",
      header: "Contact",
      cell: ({ row }) => {
        const client = row.original
        return (
          <div className="flex flex-col space-y-1">
            <div className="flex items-center text-sm">
              <Mail className="h-3 w-3 mr-1 text-blue-600" />
              {client.email}
            </div>
            <div className="flex items-center text-sm">
              <Phone className="h-3 w-3 mr-1 text-green-600" />
              {client.phone ?? "-"}
            </div>
          </div>
        )
      },
      enableSorting: true,
    },
    {
      accessorKey: "totalSpent",
      header: "Dépenses",
      cell: ({ row }) => (
        <div className="flex items-center font-medium">
          <Euro className="h-4 w-4 mr-1 text-green-600" />
          {row.original.totalSpent}€
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "lastVisit",
      header: "Dernière visite",
      cell: ({ row }) => (
        <div className="flex items-center text-sm">
          <Clock className="h-3 w-3 mr-1 text-orange-600" />
          {new Date(row.original.lastVisit).toLocaleDateString("fr-FR")}
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "fidelityPoints",
      header: "Points fidélité",
      cell: ({ row }) => <FidelityPointsCell clientId={row.original.id} />,
      enableSorting: true,
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: () => (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-border">
              {/* <DropdownMenuLabel>Actions</DropdownMenuLabel> */}
              {/* <DropdownMenuItem>Historique des RDV</DropdownMenuItem> */}
              {/* <DropdownMenuItem>Envoyer un message</DropdownMenuItem> */}
              {/* <DropdownMenuSeparator /> */}
              <DropdownMenuItem className="text-red-600  cursor-pointer focus:bg-red-600 focus:text-white">
                bloqué
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
      enableSorting: false,
    },
  ]

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-30rem)]">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <DataTable
          columns={columns}
          data={clients}
          showPagination={true}
          pageSize={10}
          emptyMessage="Aucun client trouvé."
          searchKey="" // Chaîne vide pour activer la recherche globale
          searchPlaceholder="Rechercher par nom, email, téléphone..."
        />
      </div>
    </div>
  )
}
